/**
 * Component added in ticket https://coursera.atlassian.net/browse/DEGCON-301
 * Extracted eoi buttons from static/bundles/degree-description/components/modules/hero/EoiAtAGlanceBlock.tsx
 * In order to make DDP 2.3 update more isolated
 */

/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedHTMLMessage } from 'react-intl';

import { useParams } from 'js/lib/useRouter';

import { InlineNotification, Typography2, breakpoints, typography2 } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import EoiButtonsShared from 'bundles/degree-description/components/modules/expression-of-interest/EoiButtonsShared';
import { useDegreeDescriptionPageAdmissionDeadlinesData } from 'bundles/degree-description/contexts/DegreeDescriptionPageAdmissionDeadlinesContext';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionPageElementRefs } from 'bundles/degree-description/contexts/DegreeDescriptionPageElementRefsContext';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import type { EoiTextType } from 'bundles/degree-description/utils/EoiUtils';
import { getCopy, getCtaModuleDeadlineCopy } from 'bundles/degree-description/utils/ctaModuleDeadline';
import { getCtaSocialProofText, showCtaModuleDeadline } from 'bundles/degree-description/utils/experiments';
import { getManualCopyTest } from 'bundles/degree-description/utils/manualCopyTests';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';
import { isDegreeExcludedFromShowingEOI } from 'bundles/expression-of-interest/utils';

type EoiButtonsBlockProps = {
  eoiButtons?: EoiButtonsType;
  eoiContainerText?: EoiTextType;
  trackingIndex?: number;
  setEoiTrackingIndex?: React.Dispatch<React.SetStateAction<number>>;
};

const styles = {
  container: () => css`
    margin-bottom: 0;
    background: var(--cds-color-neutral-background-primary);
    border-radius: 8px;
    padding: 24px 14px;
    ${breakpoints.up('md')} {
      box-shadow: 0 0 40px -8px var(--cds-color-darken-200), 0 0 24px -16px var(--cds-color-darken-200);
      padding: var(--cds-spacing-200);
    }
    ${breakpoints.up('lg')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-400);
    }

    .rc-Markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        font-family: ${typography2.inherit};
      }

      a {
        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-interactive-primary-selected);
        }
      }

      h2 {
        ${typography2.subtitleLarge};
        color: var(--cds-color-neutral-primary);
        margin: 0;
      }

      p {
        ${typography2.bodySecondary};
        color: var(--cds-color-neutral-primary-weak);
        margin: 0;
      }
    }
  `,
  buttonsArea: () => css`
    display: grid;
    grid-template-columns: 1fr;
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-300);

    button,
    a {
      margin-bottom: 0;
      margin-top: 0;

      &:first-child {
        margin-bottom: var(--cds-spacing-200);
      }

      &:only-child {
        margin-bottom: 0;
      }
    }

    ${breakpoints.down('sm')} {
      margin-bottom: 0;
    }
  `,
  ctaSocialProofMargin: css`
    margin-bottom: var(--cds-spacing-150);
    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
};

export const CtaModuleDeadlineText = ({ slug }: { slug: string }) => {
  const { programAdmissionDeadlinesData } = useDegreeDescriptionPageAdmissionDeadlinesData();
  const deadlineCopy = getCtaModuleDeadlineCopy(slug, programAdmissionDeadlinesData, showCtaModuleDeadline(), getCopy);

  return (
    <div data-testid="cta-module-deadline-text">
      <Typography2 component="h2" variant="subtitleLarge">
        {deadlineCopy.header}
      </Typography2>
      <Typography2 component="p" variant="bodySecondary" color="supportText">
        {deadlineCopy.body}
      </Typography2>
    </div>
  );
};

const EoiButtonsBlock = ({
  eoiButtons,
  eoiContainerText,
  trackingIndex,
  setEoiTrackingIndex,
}: EoiButtonsBlockProps) => {
  const { setFloatingEoiButtonsContainerRef } = useDegreeDescriptionPageElementRefs();
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();

  const { loadingEoiSurveyData } = useDegreeDescriptionPageData();

  const { slug } = useParams();
  // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const showCtaModuleDeadlineCopy = slug in showCtaModuleDeadline();

  // social proof on DDP experiment
  const socialProofText = getCtaSocialProofText();

  const pageSectionRef = useGetViewPageSectionRef('eoi', trackingIndex);

  const manualCopyTestObj = slug === 'ms-data-analytics-engineering-northeastern' && getManualCopyTest(slug);
  const manualCopyNortheastern =
    manualCopyTestObj && manualCopyTestObj.epicParam === 'manualCopyNortheastern' ? manualCopyTestObj : undefined;

  const getProgramInterestText = (): string => {
    // This module won't show if program interest text is not defined
    return manualCopyNortheastern?.eoiDescription ?? (eoiContainerText?.programInterestText || '');
  };

  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (isDegreeExcludedFromShowingEOI(slug) || loadingEoiSurveyData) {
    return null;
  }

  return (
    <div css={styles.container} ref={pageSectionRef}>
      {eoiContainerText?.programInterestText && (
        <div className="eoi-header-text">
          {showCtaModuleDeadlineCopy ? (
            // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            <CtaModuleDeadlineText slug={slug} />
          ) : (
            <Markdown source={getProgramInterestText()} />
          )}
        </div>
      )}
      {eoiButtons && (
        <div
          css={[styles.buttonsArea, socialProofText && styles.ctaSocialProofMargin]}
          /** since this component is rendered twice on the overview page we want to
           * only set the floatingEoiButtonsContainerRef if this component is visible which happens when:
           * 1. this component is only being rendered on mobile and the browser is currently on a mobile viewport
           * 2. this component is not rendered on mobile and the browser is not on a mobile viewport
           */
          ref={(e) => {
            setFloatingEoiButtonsContainerRef?.(e as HTMLElement);
          }}
        >
          <EoiButtonsShared
            keySuffix="-eoi-overview-button"
            eoiButtons={eoiButtons}
            eoiContainerText={eoiContainerText}
            trackingIndex={trackingIndex}
            setEoiTrackingIndex={setEoiTrackingIndex}
          />
        </div>
      )}
      {!!socialProofText && (
        <InlineNotification severity="information">
          <FormattedHTMLMessage message={socialProofText} />
        </InlineNotification>
      )}
    </div>
  );
};

export default React.memo(EoiButtonsBlock);
